import * as bootstrap from "bootstrap";

// Finds every popover trigger represented by root or nested inside root.
// Called by: initializePopovers and disposePopovers.
// Returns: An array of elements, like [button, a, span].
function findPopoverTriggers(root = document) {
  if (!root) return [];

  // querySelectorAll finds matching descendants, but it does not include root
  // itself. If root is already a popover trigger, include it first, then add any
  // popover triggers nested inside it. The optional chaining keeps this safe when
  // root is document, because document does not have matches().
  return [...(root.matches?.('[data-bs-toggle="popover"]') ? [root] : []), ...root.querySelectorAll('[data-bs-toggle="popover"]')];
}

// Finds popover trigger elements inside root and creates Bootstrap Popover instances for them.
// Called by: the turbo:load listener, the turbo:frame-render listener, and initializePopoversForTurboStream.
// Returns: Nothing.
function initializePopovers(root = document) {
  findPopoverTriggers(root).forEach((popoverTriggerEl) => bootstrap.Popover.getOrCreateInstance(popoverTriggerEl));
}

// Disposes Bootstrap Popover instances inside root so Bootstrap releases event listeners and references.
// Called by: the turbo:before-cache listener, the turbo:before-frame-render listener, and disposePopoversForTurboStream.
// Returns: Nothing.
function disposePopovers(root = document) {
  findPopoverTriggers(root).forEach((popoverTriggerEl) => bootstrap.Popover.getInstance(popoverTriggerEl)?.dispose());
}

// Removes visible popover DOM elements that Bootstrap appends outside the trigger's container.
// Called by: the turbo:before-cache listener, the turbo:before-frame-render listener, and disposePopoversForTurboStream.
// Returns: Nothing.
function removeOpenPopovers() {
  [...document.getElementsByClassName("popover")].forEach((openPopover) => openPopover.remove());
}

// Finds the DOM area affected by one Turbo Stream action and initializes popovers there.
// Called by: the custom render wrapper inside the turbo:before-stream-render listener.
// Returns: Nothing.
function initializePopoversForTurboStream(streamElement) {
  // A remove stream deletes DOM and inserts nothing, so there are no new popovers to initialize.
  if (streamElement.action === "remove") return;

  // Look at the top-level elements inside the stream's <template>, then find their live DOM
  // versions after Turbo has rendered them. This lets us initialize only the newly inserted nodes.
  const insertedRoots = [...streamElement.templateElement.content.children].map((child) => child.id && document.getElementById(child.id)).filter(Boolean);

  // If we found newly inserted elements by id, initialize popovers inside only those elements.
  if (insertedRoots.length > 0) {
    // Each inserted root may be a popover trigger itself or may contain nested popover triggers.
    insertedRoots.forEach((root) => initializePopovers(root));
    // Stop here so we do not also scan the larger Turbo Stream target container.
    return;
  }

  // If the inserted content cannot be identified by id, fall back to Turbo's target elements.
  const streamTargets = streamElement.targetElements;

  // For before/after streams, the inserted content is a sibling of the target, so scan the parent.
  if (["after", "before"].includes(streamElement.action)) {
    // Scanning the parent catches the newly inserted sibling next to the target.
    streamTargets.forEach((target) => initializePopovers(target.parentElement));
    // Stop here because before/after streams have already been handled.
    return;
  }

  // For append/prepend/replace/update streams, the target is the useful fallback area to scan.
  streamTargets.forEach((target) => initializePopovers(target));
}

// Disposes popovers before Turbo removes or replaces DOM for one Turbo Stream action.
// Called by: the custom render wrapper inside the turbo:before-stream-render listener.
// Returns: Nothing.
function disposePopoversForTurboStream(streamElement) {
  // Only these stream actions remove or overwrite existing DOM that may contain popover instances.
  if (!["remove", "replace", "update"].includes(streamElement.action)) return;

  // Dispose Bootstrap Popover instances inside each target before Turbo removes or replaces the target DOM.
  streamElement.targetElements.forEach((target) => disposePopovers(target));
  // Remove any visible popover elements Bootstrap appended elsewhere in the document.
  removeOpenPopovers();
}

// Initializes popovers after Turbo finishes a full page visit/load.
// Called by: Turbo, when it dispatches turbo:load on document.
// Returns: Nothing.
document.addEventListener("turbo:load", () => initializePopovers());

// Disposes popovers inside a frame and removes open popover DOM before Turbo renders that frame.
// Called by: Turbo, when it dispatches turbo:before-frame-render on document.
// Returns: Nothing.
document.addEventListener("turbo:before-frame-render", (event) => {
  disposePopovers(event.target);
  removeOpenPopovers();
});

// Initializes popovers inside the Turbo Frame that was just rendered.
// Called by: Turbo, when it dispatches turbo:frame-render on document.
// Returns: Nothing.
document.addEventListener("turbo:frame-render", (event) => {
  initializePopovers(event.target);
});

// Disposes popovers before Turbo saves the current page snapshot in its cache.
// Called by: Turbo, when it dispatches turbo:before-cache on document before caching a page.
// Returns: Nothing.
document.addEventListener("turbo:before-cache", () => {
  disposePopovers();
  removeOpenPopovers();
});

// Wraps Turbo Stream rendering so popovers initialize after each stream action runs.
// Called by: Turbo, when it dispatches turbo:before-stream-render once per <turbo-stream> element.
// Returns: Nothing.
document.addEventListener("turbo:before-stream-render", (event) => {
  console.debug("stream action:", event.target.action, "target:", event.target.target);

  const originalRender = event.detail.render;

  // Turbo fires turbo:before-stream-render once for each <turbo-stream> element,
  // not once for the whole response. The lazy gauge readings response currently
  // sends two streams: one append for #gauge-readings-list and one replace/remove
  // for #gauge-readings-loader. That is why this hook runs twice when a page of
  // readings loads.
  //
  // After Turbo performs each stream action, initialize popovers inside the newly
  // inserted elements when we can identify them by id, then fall back to the
  // affected stream target. Calling initializePopovers() with no argument would
  // fall back to document and rescan every popover on the page for every stream in
  // the response.
  // Runs Turbo's original stream render, then initializes popovers in the affected DOM.
  // Called by: Turbo, when it calls event.detail.render to apply the stream action.
  // Returns: Nothing.
  event.detail.render = (streamElement) => {
    disposePopoversForTurboStream(streamElement);
    originalRender(streamElement);
    initializePopoversForTurboStream(streamElement);
  };
});
